/**
 * Typed error classes for DeltaBase toolkit
 *
 * This module provides strongly-typed error classes that correspond to specific
 * error scenarios, enabling better error handling and type safety across all
 * DeltaBase packages.
 */

/**
 * Base error class for all DeltaBase errors
 */
export abstract class DeltaBaseError extends Error {
  /** HTTP status code associated with this error */
  public readonly status: number;
  /** Original error response body from the API */
  public readonly body: unknown;
  /** Error type identifier */
  public readonly errorType: string;
  /** Additional error details */
  public readonly details?: Record<string, unknown>;

  constructor(
    message: string,
    status: number,
    body: unknown,
    errorType: string,
    details?: Record<string, unknown>
  ) {
    super(message);
    this.name = this.constructor.name;
    this.status = status;
    this.body = body;
    this.errorType = errorType;
    this.details = details;
  }
}

/**
 * Error thrown when a stream version conflict occurs during append operations
 */
export class VersionConflictError extends DeltaBaseError {
  /** The current version of the stream */
  public readonly currentVersion: number;
  /** The expected version that was provided */
  public readonly expectedVersion: number;

  constructor(
    message: string,
    body: unknown,
    currentVersion: number,
    expectedVersion: number
  ) {
    super(message, 409, body, 'Version conflict', {
      currentVersion,
      expectedVersion,
    });
    this.currentVersion = currentVersion;
    this.expectedVersion = expectedVersion;
  }
}

/**
 * Error thrown when request validation fails
 */
export class ValidationError extends DeltaBaseError {
  /** Array of validation error details */
  public readonly validationErrors?: Array<{
    code: string;
    message: string;
    path: Array<string | number>;
  }>;

  constructor(
    message: string,
    body?: unknown,
    validationErrors?: Array<{
      code: string;
      message: string;
      path: Array<string | number>;
    }>
  ) {
    super(message, 400, body || message, 'Invalid request body', {
      validationErrors,
    });
    this.validationErrors = validationErrors;
  }
}

/**
 * Error thrown when an event store is not found
 */
export class EventStoreNotFoundError extends DeltaBaseError {
  /** The event store ID that was not found */
  public readonly eventStoreId: string;

  constructor(message: string, body: unknown, eventStoreId: string) {
    super(message, 404, body, 'EVENT_STORE_NOT_FOUND', {
      eventStoreId,
    });
    this.eventStoreId = eventStoreId;
  }
}

/**
 * Error thrown when a subscription is not found
 */
export class SubscriptionNotFoundError extends DeltaBaseError {
  /** The subscription ID that was not found */
  public readonly subscriptionId: string;
  /** The event store ID where the subscription was expected */
  public readonly eventStoreId: string;

  constructor(
    message: string,
    body: unknown,
    subscriptionId: string,
    eventStoreId: string
  ) {
    super(message, 404, body, 'SUBSCRIPTION_NOT_FOUND', {
      subscriptionId,
      eventStoreId,
    });
    this.subscriptionId = subscriptionId;
    this.eventStoreId = eventStoreId;
  }
}

/**
 * Error thrown when authentication fails
 */
export class AuthenticationError extends DeltaBaseError {
  constructor(message: string, body: unknown) {
    super(message, 401, body, 'AUTHENTICATION_FAILED');
  }
}

/**
 * Error thrown when authorization fails
 */
export class AuthorizationError extends DeltaBaseError {
  constructor(message: string, body: unknown) {
    super(message, 403, body, 'AUTHORIZATION_FAILED');
  }
}

/**
 * Error thrown when rate limits are exceeded
 */
export class RateLimitError extends DeltaBaseError {
  /** When the rate limit will reset (if provided) */
  public readonly retryAfter?: number;

  constructor(message: string, body: unknown, retryAfter?: number) {
    super(message, 429, body, 'RATE_LIMIT_EXCEEDED', { retryAfter });
    this.retryAfter = retryAfter;
  }
}

/**
 * Error thrown when an event store already exists during creation
 */
export class EventStoreAlreadyExistsError extends DeltaBaseError {
  /** The name of the event store that already exists */
  public readonly name: string;

  constructor(message: string, body: unknown, name: string) {
    super(message, 409, body, 'EVENT_STORE_ALREADY_EXISTS', { name });
    this.name = name;
  }
}

/**
 * Error thrown when subscription creation fails due to invalid configuration
 */
export class InvalidSubscriptionConfigError extends DeltaBaseError {
  /** The specific configuration error details */
  public readonly configError: string;

  constructor(message: string, body: unknown, configError: string) {
    super(message, 400, body, 'INVALID_SUBSCRIPTION_CONFIG', {
      configError,
    });
    this.configError = configError;
  }
}

/**
 * Error thrown when operations timeout
 */
export class TimeoutError extends DeltaBaseError {
  /** The timeout duration in milliseconds */
  public readonly timeoutMs: number;
  /** The operation that timed out */
  public readonly operation: string;

  constructor(
    message: string,
    body: unknown,
    timeoutMs: number,
    operation: string
  ) {
    super(message, 408, body, 'TIMEOUT_ERROR', {
      timeoutMs,
      operation,
    });
    this.timeoutMs = timeoutMs;
    this.operation = operation;
  }
}

/**
 * Error thrown when storage operations fail
 */
export class StorageError extends DeltaBaseError {
  /** The type of storage that failed */
  public readonly storageType: 'r2' | 'sqlite' | 'archive';
  /** The operation that failed */
  public readonly operation: 'read' | 'write' | 'delete';

  constructor(
    message: string,
    body: unknown,
    storageType: 'r2' | 'sqlite' | 'archive',
    operation: 'read' | 'write' | 'delete'
  ) {
    super(message, 500, body, 'STORAGE_ERROR', {
      storageType,
      operation,
    });
    this.storageType = storageType;
    this.operation = operation;
  }
}

/**
 * Error thrown when a specific stream is not found
 */
export class StreamNotFoundError extends DeltaBaseError {
  /** The stream ID that was not found */
  public readonly streamId: string;
  /** The event store ID where the stream was expected */
  public readonly eventStoreId: string;

  constructor(
    message: string,
    body: unknown,
    streamId: string,
    eventStoreId: string
  ) {
    super(message, 404, body, 'STREAM_NOT_FOUND', {
      streamId,
      eventStoreId,
    });
    this.streamId = streamId;
    this.eventStoreId = eventStoreId;
  }
}

/**
 * Error thrown when serialization/deserialization fails
 */
export class SerializationError extends DeltaBaseError {
  /** The type of data that failed to serialize/deserialize */
  public readonly dataType: 'event' | 'metadata' | 'filter' | 'webhook_payload';
  /** The original data that failed to process (if safe to include) */
  public readonly originalData?: string;

  constructor(
    message: string,
    body: unknown,
    dataType: 'event' | 'metadata' | 'filter' | 'webhook_payload',
    originalData?: string
  ) {
    super(message, 400, body, 'SERIALIZATION_ERROR', {
      dataType,
      originalData,
    });
    this.dataType = dataType;
    this.originalData = originalData;
  }
}

/**
 * Error thrown for general server errors
 */
export class InternalServerError extends DeltaBaseError {
  constructor(message: string, body: unknown) {
    super(message, 500, body, 'INTERNAL_SERVER_ERROR');
  }
}

/**
 * Error thrown when an operation is attempted in an illegal or inappropriate state
 */
export class IllegalStateError extends DeltaBaseError {
  /** The current state that prevents the operation */
  public readonly currentState?: string;
  /** The operation that was attempted */
  public readonly attemptedOperation?: string;

  constructor(
    message: string,
    body: unknown,
    currentState?: string,
    attemptedOperation?: string
  ) {
    super(message, 400, body, 'ILLEGAL_STATE', {
      currentState,
      attemptedOperation,
    });
    this.currentState = currentState;
    this.attemptedOperation = attemptedOperation;
  }
}

/**
 * Error thrown when a generic not found scenario occurs
 */
export class NotFoundError extends DeltaBaseError {
  /** The ID of the resource that was not found */
  public readonly resourceId?: string;
  /** The type of resource that was not found */
  public readonly resourceType?: string;

  constructor(options?: {
    id?: string;
    type?: string;
    message?: string;
    body?: unknown;
  }) {
    const message =
      options?.message ??
      (options?.id && options?.type
        ? `${options.type} with ID '${options.id}' was not found`
        : options?.type
          ? `${options.type} was not found`
          : 'Resource was not found');

    super(message, 404, options?.body || message, 'NOT_FOUND', {
      resourceId: options?.id,
      resourceType: options?.type,
    });

    this.resourceId = options?.id;
    this.resourceType = options?.type;
  }
}

/**
 * Type guard to check if an error is a DeltaBase error
 */
export function isDeltaBaseError(error: unknown): error is DeltaBaseError {
  return error instanceof DeltaBaseError;
}

/**
 * Type guard to check if an error is a version conflict error
 */
export function isVersionConflictError(
  error: unknown
): error is VersionConflictError {
  return error instanceof VersionConflictError;
}

/**
 * Type guard to check if an error is a validation error
 */
export function isValidationError(error: unknown): error is ValidationError {
  return error instanceof ValidationError;
}

/**
 * Type guard to check if an error is an event store not found error
 */
export function isEventStoreNotFoundError(
  error: unknown
): error is EventStoreNotFoundError {
  return error instanceof EventStoreNotFoundError;
}

/**
 * Type guard to check if an error is a subscription not found error
 */
export function isSubscriptionNotFoundError(
  error: unknown
): error is SubscriptionNotFoundError {
  return error instanceof SubscriptionNotFoundError;
}

/**
 * Type guard to check if an error is an authentication error
 */
export function isAuthenticationError(
  error: unknown
): error is AuthenticationError {
  return error instanceof AuthenticationError;
}

/**
 * Type guard to check if an error is an authorization error
 */
export function isAuthorizationError(
  error: unknown
): error is AuthorizationError {
  return error instanceof AuthorizationError;
}

/**
 * Type guard to check if an error is a rate limit error
 */
export function isRateLimitError(error: unknown): error is RateLimitError {
  return error instanceof RateLimitError;
}

/**
 * Type guard to check if an error is an event store already exists error
 */
export function isEventStoreAlreadyExistsError(
  error: unknown
): error is EventStoreAlreadyExistsError {
  return error instanceof EventStoreAlreadyExistsError;
}

/**
 * Type guard to check if an error is an invalid subscription config error
 */
export function isInvalidSubscriptionConfigError(
  error: unknown
): error is InvalidSubscriptionConfigError {
  return error instanceof InvalidSubscriptionConfigError;
}

/**
 * Type guard to check if an error is a timeout error
 */
export function isTimeoutError(error: unknown): error is TimeoutError {
  return error instanceof TimeoutError;
}

/**
 * Type guard to check if an error is a storage error
 */
export function isStorageError(error: unknown): error is StorageError {
  return error instanceof StorageError;
}

/**
 * Type guard to check if an error is a stream not found error
 */
export function isStreamNotFoundError(
  error: unknown
): error is StreamNotFoundError {
  return error instanceof StreamNotFoundError;
}

/**
 * Type guard to check if an error is a serialization error
 */
export function isSerializationError(
  error: unknown
): error is SerializationError {
  return error instanceof SerializationError;
}

/**
 * Type guard to check if an error is a not found error
 */
export function isNotFoundError(error: unknown): error is NotFoundError {
  return error instanceof NotFoundError;
}

/**
 * Type guard to check if an error is an internal server error
 */
export function isInternalServerError(
  error: unknown
): error is InternalServerError {
  return error instanceof InternalServerError;
}

/**
 * Type guard to check if an error is an illegal state error
 */
export function isIllegalStateError(
  error: unknown
): error is IllegalStateError {
  return error instanceof IllegalStateError;
}

/**
 * Error thrown when expected stream version doesn't match current version
 * This is a specialized version conflict error for stream-based operations
 */
export class StreamVersionConflictError<
  T = unknown,
> extends VersionConflictError {
  /** The current version that was found */
  public readonly current: T;
  /** The expected version that was provided */
  public readonly expected: T;

  constructor(current: T, expected: T, message?: string) {
    const currentVersion = typeof current === 'number' ? current : -1;
    const expectedVersion = typeof expected === 'number' ? expected : -1;

    super(
      message ??
        `Expected stream version ${expected} does not match current ${current}`,
      { current, expected },
      currentVersion,
      expectedVersion
    );

    this.current = current;
    this.expected = expected;
  }
}

/**
 * Type guard to check if an error is a stream version conflict error
 */
export function isStreamVersionConflictError(
  error: unknown
): error is StreamVersionConflictError {
  return error instanceof StreamVersionConflictError;
}

/**
 * Union type of all possible DeltaBase errors
 */
export type DeltaBaseErrorTypes =
  | VersionConflictError
  | ValidationError
  | EventStoreNotFoundError
  | SubscriptionNotFoundError
  | AuthenticationError
  | AuthorizationError
  | IllegalStateError
  | RateLimitError
  | EventStoreAlreadyExistsError
  | InvalidSubscriptionConfigError
  | TimeoutError
  | StorageError
  | StreamNotFoundError
  | SerializationError
  | InternalServerError
  | NotFoundError
  | StreamVersionConflictError;

// Validation error codes
export enum ValidationErrors {
  NOT_A_NONEMPTY_STRING = 'NOT_A_NONEMPTY_STRING',
  NOT_A_POSITIVE_NUMBER = 'NOT_A_POSITIVE_NUMBER',
  NOT_AN_UNSIGNED_BIGINT = 'NOT_AN_UNSIGNED_BIGINT',
}

// Utility functions for validation
export const isNumber = (val: unknown): val is number =>
  typeof val === 'number' && !Number.isNaN(val);

export const isString = (val: unknown): val is string =>
  typeof val === 'string';

export const assertNotEmptyString = (value: unknown): string => {
  if (!isString(value) || value.length === 0) {
    throw new ValidationError(ValidationErrors.NOT_A_NONEMPTY_STRING);
  }
  return value;
};

export const assertPositiveNumber = (value: unknown): number => {
  if (!isNumber(value) || value <= 0) {
    throw new ValidationError(ValidationErrors.NOT_A_POSITIVE_NUMBER);
  }
  return value;
};

export const assertUnsignedBigInt = (value: string): bigint => {
  const number = BigInt(value);
  if (number < 0) {
    throw new ValidationError(ValidationErrors.NOT_AN_UNSIGNED_BIGINT);
  }
  return number;
};
